import { ComponentProps } from 'preact';

import { cn } from '@/lib/utils';

import { useProductCard } from './context';

type ProductCardWhatsIncludedProps = ComponentProps<'div'>;

/**
 * The card's second face: what the money buys, itemised.
 *
 * Always white, even on a card whose front is beige — the panel is a change of subject, not a
 * continuation of the offer, and the designs show it that way on every card.
 *
 * Hidden rather than unmounted, so the panel is measurable before it is shown and the height
 * animation has somewhere to animate to. That means its controls are real, so `inert` goes with the
 * `aria-hidden`: without it the Back button and the purchase button underneath stay in the tab
 * order while off-screen, which is both a keyboard trap for anyone tabbing through the card and the
 * ARIA rule against hiding focusable content. It also keeps them out of the running when the modal
 * around this decides what to focus on open.
 */
const ProductCardWhatsIncluded = ({ className, children, ...props }: ProductCardWhatsIncludedProps) => {
  const { showWhatsIncluded, whatsIncludedRef } = useProductCard();
  return (
    <div
      aria-hidden={!showWhatsIncluded}
      inert={!showWhatsIncluded}
      className="absolute inset-0 bg-white transition-transform aria-hidden:pointer-events-none aria-hidden:translate-x-full"
    >
      <div
        ref={whatsIncludedRef}
        className={cn('flex flex-col items-center gap-3.5 px-4 pb-7 pt-5', className)}
        // eslint-disable-next-line react/jsx-props-no-spreading
        {...props}
      >
        {children}
      </div>
    </div>
  );
};

export default ProductCardWhatsIncluded;
